import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getApps, initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Module-level flag — prevents double-connection on Vite hot reload.
const firestoreEmulatorConnected = new Set();

const getFirebaseApp = (firebaseConfig, firebaseAppName) => {
  const firebaseApps = getApps();
  return (
    firebaseApps.find(a => a.name === firebaseAppName) ||
    initializeApp(firebaseConfig, firebaseAppName)
  );
};

const useFirebaseApp = (firebaseConfig, firebaseAppName) => {
  const [firebaseApp, setFirebaseApp] = useState(() =>
    getFirebaseApp(firebaseConfig, firebaseAppName),
  );

  useEffect(() => {
    setFirebaseApp(() => getFirebaseApp(firebaseConfig, firebaseAppName));
  }, [firebaseConfig, firebaseAppName]);

  return firebaseApp;
};

const FirebaseContext = React.createContext();
const providerSignature = Symbol('FirebaseProvider');

const FirebaseProvider = ({ firebaseConfig, firebaseAppName, firestoreDbId, children }) => {
  const firebaseApp = useFirebaseApp(firebaseConfig, firebaseAppName);

  const db = useMemo(() => {
    const firestore = getFirestore(firebaseApp, firestoreDbId);
    if (import.meta.env.VITE_ENV === 'local' && !firestoreEmulatorConnected.has(firebaseApp)) {
      connectFirestoreEmulator(firestore, 'localhost', 8080);
      firestoreEmulatorConnected.add(firebaseApp);
    }
    return firestore;
  }, [firebaseApp, firestoreDbId]);

  const storage = useMemo(() => getStorage(firebaseApp), [firebaseApp]);

  const firebaseContext = useMemo(
    () => ({ signature: providerSignature, firebaseApp, db, storage }),
    [firebaseApp, db, storage],
  );

  return <FirebaseContext.Provider value={firebaseContext}>{children}</FirebaseContext.Provider>;
};

FirebaseProvider.propTypes = {
  firebaseConfig: PropTypes.shape({
    apiKey: PropTypes.string.isRequired,
    authDomain: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
  }).isRequired,
  firebaseAppName: PropTypes.string,
  firestoreDbId: PropTypes.string,
  children: PropTypes.element.isRequired,
};

FirebaseProvider.defaultProps = {
  firebaseAppName: '[DEFAULT]',
  firestoreDbId: '(default)',
};

const useFirebaseContext = () => {
  const context = useContext(FirebaseContext) || {};
  if (context.signature !== providerSignature) {
    throw new Error('useFirebaseContext must be a descendant of <FirebaseProvider/>');
  }
  return {
    firebaseApp: context.firebaseApp,
    db: context.db,
    storage: context.storage,
  };
};

const useFirebase = () => useFirebaseContext().firebaseApp;
const useFirestore = () => useFirebaseContext().db;
const useStorage = () => useFirebaseContext().storage;

export { useFirebase, useFirestore, useStorage, useFirebaseContext, FirebaseProvider };
